import React, { useCallback, useEffect, useRef, useState } from 'react'
import { List, ListItem } from '@astryxdesign/core/List'
import { DropdownMenu } from '@astryxdesign/core/DropdownMenu'

export interface SettingsSectionNavItem<Id extends string> {
  id: Id
  label: string
  icon: React.ReactNode
}

interface SettingsSectionNavProps<Id extends string> {
  sections: SettingsSectionNavItem<Id>[]
  activeId: Id
  onSelect: (id: Id) => void
  /** Accessible name for the navigation landmark the list sits in. */
  label: string
  /** Matches each dialog's existing list density; the two do not agree. */
  density?: 'balanced' | 'spacious'
}

/**
 * The section switcher shared by the story settings and application settings dialogs.
 *
 * On a wide screen this is the 220px sidebar of a two-pane dialog and the list never runs out
 * of room. On a phone the same list is laid out as a horizontal strip, and once the sections
 * stop fitting the strip becomes a sideways scroller -- which hides sections behind a gesture
 * nothing on screen advertises. Past that point the list is replaced by a dropdown naming the
 * section you are on, which shows every section at once and costs one tap.
 *
 * The swap is driven by measurement rather than a breakpoint, because whether the sections fit
 * depends on how many there are (story settings grows a Chapters entry once a story has two
 * chapters) and how long their labels are, not on the width alone.
 */
export default function SettingsSectionNav<Id extends string>({
  sections,
  activeId,
  onSelect,
  label,
  density = 'balanced'
}: SettingsSectionNavProps<Id>): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  // The list stays mounted and measurable whichever way this renders. Unmounting it when the
  // dropdown takes over would destroy the measurement that produced the dropdown, and the next
  // observation would report a container with nothing in it -- so the strip would come back,
  // overflow again, and the two would trade places forever. Out of flow but still laid out, its
  // width keeps answering the only question being asked: would the sections fit if shown?
  const measure = useCallback(() => {
    const container = containerRef.current
    const list = listRef.current
    if (!container || !list) return
    // scrollWidth rather than the bounding box: the phone rules give the list width: max-content,
    // so this is the width the sections actually want, not the width they were granted.
    setIsOverflowing(list.scrollWidth > container.clientWidth)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    const list = listRef.current
    if (!container || !list || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    // Both boxes matter: the container changes with the viewport, and the list changes when a
    // section is added or a webfont finishes loading and relays the labels.
    observer.observe(container)
    observer.observe(list)
    measure()
    return () => observer.disconnect()
  }, [measure])

  // Re-measure when the set of sections changes, ahead of any resize the observer would see.
  useEffect(measure, [measure, sections])

  const active = sections.find((section) => section.id === activeId) ?? sections[0]

  return (
    <div ref={containerRef} className="settings-section-nav">
      <div
        ref={listRef}
        className={
          isOverflowing ? 'settings-section-nav-list is-measuring' : 'settings-section-nav-list'
        }
        // Hidden from the accessibility tree and from focus while it is only a ruler. Astryx's
        // List is a set of real buttons; leaving them reachable would put a second, invisible
        // copy of every section in the tab order behind the dropdown that replaced them.
        aria-hidden={isOverflowing || undefined}
        inert={isOverflowing || undefined}
      >
        <List density={density}>
          {sections.map((section) => (
            <ListItem
              key={section.id}
              label={section.label}
              startContent={section.icon}
              isSelected={section.id === activeId}
              onClick={() => onSelect(section.id)}
            />
          ))}
        </List>
      </div>

      {isOverflowing && active ? (
        <DropdownMenu
          button={{
            variant: 'secondary',
            label: active.label,
            icon: active.icon,
            className: 'settings-section-nav-trigger'
          }}
          aria-label={label}
          items={sections.map((section) => ({
            label: section.label,
            icon: section.icon,
            onClick: () => onSelect(section.id)
          }))}
        />
      ) : null}
    </div>
  )
}
